export const SPANISH_LOCALE = "es" as const;

export type SpanishLocale = typeof SPANISH_LOCALE;

export type SpanishOverride = {
  text: string;
  source: string;
};

export type FieldSpanishOverrides = {
  label?: SpanishOverride;
  placeholder?: SpanishOverride;
  yesLabel?: SpanishOverride;
  noLabel?: SpanishOverride;
  bullets?: Array<SpanishOverride | null>;
  options?: Record<string, SpanishOverride>;
};

export type FormSpanishOverrides = {
  title?: SpanishOverride;
  description?: SpanishOverride;
  sections?: Record<
    string,
    { name?: SpanishOverride; description?: SpanishOverride }
  >;
  fields?: Record<string, FieldSpanishOverrides>;
};

export type SpanishSourceField = {
  id: string;
  label: string;
  kind: string;
  options?: unknown;
  settings?: unknown;
  locked?: boolean;
};

export type SpanishSourceSection = {
  id: string;
  name: string;
  description?: string | null;
  fields: SpanishSourceField[];
};

export type SpanishSourceForm = {
  title: string;
  description?: string | null;
  sections: SpanishSourceSection[];
};

export type SpanishLine = {
  key: string;
  english: string;
  hash: string;
  locked: boolean;
};

export type SpanishOrigin = "override" | "cache" | "missing";

export { SPANISH_CHROME } from "./spanish-chrome";

export function normalizeTranslatableText(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export function translationSourceHash(value: string): string {
  const normalized = normalizeTranslatableText(value);
  let hash = BigInt("0xcbf29ce484222325");
  const prime = BigInt("0x100000001b3");
  const mask = BigInt("0xffffffffffffffff");
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= BigInt(normalized.charCodeAt(index));
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, "0");
}

export function parseSpanishOverrides(value: unknown): FormSpanishOverrides {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as FormSpanishOverrides;
}
export function resolveSpanishLine(input: {
  english: string;
  override?: SpanishOverride | null;
  cacheText?: string | null;
}): { text: string | null; origin: SpanishOrigin } {
  const english = normalizeTranslatableText(input.english);
  if (!english) return { text: null, origin: "missing" };
  const override = input.override;
  if (
    override &&
    normalizeTranslatableText(override.text) &&
    normalizeTranslatableText(override.source) === english
  ) {
    return { text: override.text.trim(), origin: "override" };
  }
  const cached = input.cacheText?.trim();
  if (cached) return { text: cached, origin: "cache" };
  return { text: null, origin: "missing" };
}

function line(
  key: string,
  english: string | null | undefined,
  locked: boolean,
): SpanishLine | null {
  const text = english?.trim();
  if (!text) return null;
  return {
    key,
    english: text,
    hash: translationSourceHash(text),
    locked,
  };
}

function optionLabels(options: unknown): Array<{ id: string; label: string }> {
  if (!Array.isArray(options)) return [];
  return options.flatMap((option, index) => {
    if (typeof option === "string" && option.trim()) {
      return [{ id: `option_${index}`, label: option.trim() }];
    }
    if (!option || typeof option !== "object") return [];
    const record = option as Record<string, unknown>;
    if (typeof record.label !== "string" || !record.label.trim()) return [];
    const id =
      typeof record.id === "string" && record.id
        ? record.id
        : `option_${index}`;
    return [{ id, label: record.label.trim() }];
  });
}

export function settingsRecord(settings: unknown): Record<string, unknown> {
  return settings && typeof settings === "object"
    ? (settings as Record<string, unknown>)
    : {};
}

export function collectSpanishLines(form: SpanishSourceForm): SpanishLine[] {
  const lines: SpanishLine[] = [];
  const push = (entry: SpanishLine | null) => {
    if (entry) lines.push(entry);
  };
  push(line("form:title", form.title, false));
  push(line("form:description", form.description, false));
  for (const section of form.sections) {
    const sectionLocked = section.fields.every((field) => field.locked);
    push(line(`section:${section.id}:name`, section.name, sectionLocked));
    push(
      line(
        `section:${section.id}:description`,
        section.description,
        sectionLocked,
      ),
    );
    for (const field of section.fields) {
      const locked = field.locked === true;
      push(line(`field:${field.id}:label`, field.label, locked));
      const settings = settingsRecord(field.settings);
      push(
        line(
          `field:${field.id}:placeholder`,
          typeof settings.placeholder === "string" ? settings.placeholder : null,
          locked,
        ),
      );
      push(
        line(
          `field:${field.id}:yes`,
          typeof settings.yesLabel === "string" ? settings.yesLabel : null,
          locked,
        ),
      );
      push(
        line(
          `field:${field.id}:no`,
          typeof settings.noLabel === "string" ? settings.noLabel : null,
          locked,
        ),
      );
      if (Array.isArray(settings.bullets)) {
        settings.bullets.forEach((bullet, index) => {
          push(
            line(
              `field:${field.id}:bullet:${index}`,
              typeof bullet === "string" ? bullet : null,
              locked,
            ),
          );
        });
      }
      for (const option of optionLabels(field.options)) {
        push(
          line(`field:${field.id}:option:${option.id}`, option.label, locked),
        );
      }
    }
  }
  return lines;
}

export function overrideForLine(
  overrides: FormSpanishOverrides,
  key: string,
): SpanishOverride | null {
  if (key === "form:title") return overrides.title ?? null;
  if (key === "form:description") return overrides.description ?? null;
  const section = key.match(/^section:([^:]+):(name|description)$/);
  if (section) {
    const sectionId = section[1] ?? "";
    const entry = overrides.sections?.[sectionId];
    return section[2] === "name"
      ? (entry?.name ?? null)
      : (entry?.description ?? null);
  }
  const field = key.match(/^field:([^:]+):([a-z]+)(?::(.+))?$/);
  if (!field) return null;
  const fieldId = field[1] ?? "";
  const entry = overrides.fields?.[fieldId];
  if (!entry) return null;
  const slot = field[2];
  const rest = field[3];
  if (slot === "label") return entry.label ?? null;
  if (slot === "placeholder") return entry.placeholder ?? null;
  if (slot === "yes") return entry.yesLabel ?? null;
  if (slot === "no") return entry.noLabel ?? null;
  if (slot === "bullet" && rest) {
    return entry.bullets?.[Number(rest)] ?? null;
  }
  if (slot === "option" && rest) return entry.options?.[rest] ?? null;
  return null;
}

export function setOverrideForLine(
  overrides: FormSpanishOverrides,
  key: string,
  english: string,
  text: string,
): FormSpanishOverrides {
  const next: FormSpanishOverrides = {
    ...overrides,
    sections: { ...overrides.sections },
    fields: { ...overrides.fields },
  };
  const trimmed = text.trim();
  const write = (_current: SpanishOverride | undefined): SpanishOverride | undefined => {
    if (!trimmed) return undefined;
    return { text: trimmed, source: english };
  };
  if (key === "form:title") {
    next.title = write(overrides.title);
    return next;
  }
  if (key === "form:description") {
    next.description = write(overrides.description);
    return next;
  }
  const section = key.match(/^section:([^:]+):(name|description)$/);
  if (section) {
    const id = section[1] ?? "";
    const entry = { ...next.sections?.[id] };
    if (section[2] === "name") entry.name = write(entry.name);
    else entry.description = write(entry.description);
    next.sections = { ...next.sections, [id]: entry };
    return next;
  }
  const field = key.match(/^field:([^:]+):([a-z]+)(?::(.+))?$/);
  if (!field) return overrides;
  const id = field[1] ?? "";
  const entry: FieldSpanishOverrides = { ...next.fields?.[id] };
  const slot = field[2];
  const rest = field[3];
  if (slot === "label") entry.label = write(entry.label);
  else if (slot === "placeholder") entry.placeholder = write(entry.placeholder);
  else if (slot === "yes") entry.yesLabel = write(entry.yesLabel);
  else if (slot === "no") entry.noLabel = write(entry.noLabel);
  else if (slot === "bullet" && rest) {
    const bullets = [...(entry.bullets ?? [])];
    bullets[Number(rest)] = write(bullets[Number(rest)] ?? undefined) ?? null;
    entry.bullets = bullets;
  } else if (slot === "option" && rest) {
    const options = { ...entry.options };
    const written = write(options[rest]);
    if (written) options[rest] = written;
    else delete options[rest];
    entry.options = options;
  }
  next.fields = { ...next.fields, [id]: entry };
  return next;
}
